import React, { useContext, useEffect } from "react";
import styled from "styled-components";
import { ColorClassNames, ColorVariables } from "manifest-components/src/Colors";
import ReactDOM from "react-dom";
import { Button } from "manifest-components/src/Button";
import { Container, FlexContainer, Spacing } from "manifest-components/src/Containers";
import NextGenImage from "manifest-components/src/Images/NextGenImage";
import { Input } from "manifest-components/src/Inputs/Input";
import ErrorMessage from "manifest-components/src/Form/ErrorMessage";
import DisclaimerAgreeToTermsOfServiceLogin from "manifest-components/src/LegalDisclaimer/employerDashboardDisclaimers/DisclaimerAgreeToTermsOfServiceLogin";
import AppContainer from "../../components/Layout/AppContainer";
import type { WelcomeControllerType } from "./WelcomeController";
import { AppContext } from "../../context/App/appProvider";
import WelcomeVector from "./manifest-employer-welcome-vector.png";
import WelcomeVectorWebp from "./manifest-employer-welcome-vector.webp";

const StyledAppContainer = styled(AppContainer)`
  height: 100%;
`;

const col2Size = 500;

const StyledBackground = styled.div`
  background: ${ColorVariables.orange9};
  z-index: 0;
  pointer-events: none;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  position: fixed;
  .col2 {
    background: #fff6ef;
    height: 100%;
    width: 200%;
    position: absolute;
    top: 0;
    right: 0;
    left: calc(100% - ${col2Size}px);
  }
`;

const StyledWelcomeScreen = styled.div`
  .or {
    position: relative;
    .text {
      position: relative;
      background: #fff6ef;
      padding-left: 15px;
      padding-right: 15px;
    }
    &:before {
      z-index: 0;
      border-top: 1px solid rgba(255, 232, 200, 0.9);
      content: " ";
      top: 50%;
      left: 0;
      width: 100%;
      display: block;
      position: absolute;
    }
  }
  .twoColumns {
    display: flex;
    width: 100%;
    > *:first-child {
      flex: 1;
    }
    > *:last-child {
      padding-left: 70px;
      padding-right: 70px;
      width: ${col2Size}px;
    }
  }

  img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const StyledForgotPasswordContainer = styled(FlexContainer)`
  margin-top: 6px;
  margin-right: 40px;
  button {
    width: auto;
    text-align: right;
  }
`;

const TwoColumnBackground = () => {
  return ReactDOM.createPortal(
    <StyledBackground>
      <StyledAppContainer>
        <div className="col2" />
      </StyledAppContainer>
    </StyledBackground>,
    document.querySelector("#appGlobal")!
  );
};

export default function WelcomeScreen(props: WelcomeControllerType) {
  const { setHeaderClass } = useContext(AppContext);
  const {
    errorMessage,
    highlightForm,
    setEmail,
    email,
    handleForgotPassword,
    allowLogin,
    error,
    passwordError,
    setPassword,
    password,
    goToAccountCreationScreen,
    submitLoginForm,
  } = props;

  useEffect(() => {
    setHeaderClass("noBackground");
    return () => {
      setHeaderClass("");
    };
  }, []);

  return (
    <StyledWelcomeScreen>
      <form onSubmit={allowLogin ? submitLoginForm : undefined}>
        <TwoColumnBackground />
        <AppContainer>
          <div className="twoColumns">
            <div>
              <h1>
                Supercharge your <br /> retirement benefits.
              </h1>
              <Spacing space={60} />
              <NextGenImage srcWebp={WelcomeVectorWebp} src={WelcomeVector} alt="Manifest" />
            </div>
            <div>
              <div className="col2">
                <h3 className="center-text">Join Manifest today.</h3>
                <Spacing space={30} />
                <Button name="createAccount" text="Get started" onClick={goToAccountCreationScreen} />
                <Spacing space={20} />
                <p className="xsmall center-text bold or">
                  <span className="text">or</span>
                </p>
                <Spacing space={20} />
                <p className="bold center-text">Log in</p>
                <Spacing space={30} />
                <Container className="tile-section-a">
                  <ErrorMessage>{errorMessage}</ErrorMessage>
                  <Input
                    name="email"
                    autoComplete
                    error={highlightForm || error}
                    onChange={setEmail}
                    title="Email"
                    value={email}
                  />
                  <Input
                    name="password"
                    autoComplete
                    data-head-redact-text="true"
                    error={passwordError}
                    onChange={setPassword}
                    title="Password"
                    type="password"
                    value={password}
                  />
                  <StyledForgotPasswordContainer>
                    <button data-name="forgotPassword" onClick={handleForgotPassword} type="button">
                      <p className={`${ColorClassNames.mint} small bold`}>Forgot password?</p>
                    </button>
                  </StyledForgotPasswordContainer>
                </Container>
                <Spacing space={30} />
                <Button name="login" type="submit" disabled={!allowLogin} text="Log in" />
                <Spacing space={15} />
                <DisclaimerAgreeToTermsOfServiceLogin />
              </div>
            </div>
          </div>
        </AppContainer>
      </form>
    </StyledWelcomeScreen>
  );
}
